import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ObserverNav from '../components/ObserverNav';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Video, Mic, MicOff, Loader2, Copy, Check, PhoneOff, AlertCircle, ExternalLink, FileText, Sparkles, Link2, MessageCircle, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const ObserverVideoCall = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Form state
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [loadingChildren, setLoadingChildren] = useState(true);

  // Call state: idle | awaiting_url | link_ready | connecting | live | uploading | done
  const [callState, setCallState] = useState('idle');
  const [roomName, setRoomName] = useState('');
  const [roomUrl, setRoomUrl] = useState('');
  const [meetLinkInput, setMeetLinkInput] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState('');
  const [reportId, setReportId] = useState(null);
  const [parentNotified, setParentNotified] = useState(false);

  // Refs to keep MediaRecorder & timer outside React render
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('observer_token') : null;

  // ---- Fetch observer's assigned children ----
  const fetchChildren = useCallback(async () => {
    if (!token) {
      navigate('/observer/login');
      return;
    }
    setLoadingChildren(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/observer/children?token=${token}`);
      const list = res.data?.children || [];
      setChildren(list);
      if (list.length === 1) setSelectedChildId(list[0].id);
    } catch (err) {
      if (err.response?.status === 401) navigate('/observer/login');
    } finally {
      setLoadingChildren(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  // ---- Step 1: Open Google Meet in a new tab ----
  const generateLink = () => {
    setError('');
    setParentNotified(false);
    setMeetLinkInput('');
    if (!selectedChildId) {
      setError('Please pick a child to call.');
      return;
    }
    window.open('https://meet.google.com/new', '_blank', 'noopener,noreferrer');
    setCallState('awaiting_url');
  };

  // ---- Step 1b: Observer pastes the Google Meet URL back ----
  const confirmMeetUrl = async () => {
    const url = meetLinkInput.trim();
    if (!url || !url.includes('meet.google.com/')) {
      setError('Please paste a valid Google Meet link — it should look like https://meet.google.com/xxx-xxxx-xxx');
      return;
    }
    setError('');
    const meetCode = url.split('/').pop().split('?')[0] || `gmeet-${Date.now().toString(36)}`;
    setRoomName(meetCode);
    setRoomUrl(url);
    setCallState('link_ready');

    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('child_id', selectedChildId);
      fd.append('room_url', url);
      const res = await axios.post(`${BACKEND_URL}/api/observer/video-call/notify-parent`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 10000,
      });
      if (res.data?.success) setParentNotified(true);
    } catch (_) {
      // notification failure must not block the call flow
    }
  };

  // ---- Step 2: Start recording ----
  const joinAndRecord = async () => {
    setError('');
    setCallState('connecting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'audio/mp4';

      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setRecording(true);
      setCallState('live');
      setElapsed(0);

      timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    } catch (err) {
      setCallState('link_ready');
      setError(
        err?.name === 'NotAllowedError'
          ? 'Microphone permission denied. Please allow microphone access to record the call.'
          : `Could not start recording: ${err?.message || err}`
      );
    }
  };

  // ---- Stop recording, upload to existing pipeline ----
  const endCall = async () => {
    if (callState !== 'live') return;
    setCallState('uploading');

    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }

    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      await new Promise((resolve) => { mr.onstop = resolve; mr.stop(); });
    }
    setRecording(false);

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }

    const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = [];

    if (audioBlob.size < 1024) {
      setError('No audio was captured. Please ensure your microphone is working and try again.');
      setCallState('idle');
      return;
    }

    const child = children.find((c) => c.id === selectedChildId);
    const formData = new FormData();
    formData.append('token', token);
    formData.append('audio_file', audioBlob, `videocall-${roomName}.webm`);
    formData.append('child_id', selectedChildId);
    if (child?.name) formData.append('child_name', child.name);
    if (child?.gender) formData.append('gender', child.gender);
    formData.append('call_id', roomName);
    formData.append('source_type', 'video_call');

    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/observer/generate-call-report-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 }
      );
      const jobId = res.data?.job_id;
      // Transcription + AI analysis run in the background and can take a few
      // minutes — poll for completion rather than holding one long request open,
      // since the hosting platform's proxy kills any single request past ~120s.
      const job = await pollReportJob(jobId);
      setReportId(job.report_id || null);
      setCallState('done');
      toast({
        title: 'Report generated',
        description: "The transcript was processed and a Day's Insights report is ready in your reports.",
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate report from call audio.');
      setCallState('idle');
    }
  };

  const POLL_INTERVAL_MS = 4000;
  const MAX_POLL_MS = 10 * 60 * 1000; // give up after 10 minutes

  const pollReportJob = async (jobId) => {
    const startedAt = Date.now();
    let consecutiveErrors = 0;
    while (Date.now() - startedAt < MAX_POLL_MS) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/observer/generate-call-report-status/${jobId}`,
          { params: { token }, timeout: 15000 },
        );
        consecutiveErrors = 0;
        const job = res.data;
        if (job.status === 'completed') return job;
        if (job.status === 'failed') throw new Error(job.error || 'Report generation failed');
      } catch (e) {
        if (!e.isAxiosError) throw e; // job explicitly failed — surface it now
        if (e.response && e.response.status === 404) throw e; // job vanished — unrecoverable
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) throw e; // repeated network errors — give up
      }
    }
    throw new Error('Report generation is taking longer than expected. Check My Reports in a few minutes.');
  };

  const cancelCall = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    setRecording(false);
    setCallState('idle');
    setRoomName('');
    setRoomUrl('');
    setMeetLinkInput('');
    setElapsed(0);
    setParentNotified(false);
    chunksRef.current = [];
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (e) { /* ignore */ } }
      if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const copyInviteLink = async () => {
    if (!roomUrl) return;
    try {
      await navigator.clipboard.writeText(roomUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (e) {
      setError('Could not copy link. Please copy it manually.');
    }
  };

  const selectedChild = children.find((c) => c.id === selectedChildId);

  return (
    <div className="min-h-screen bg-slate-50">
      <ObserverNav />

      <main className="pt-20 pb-8 px-4">
        <div className="max-w-5xl mx-auto">

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Video className="w-5 h-5 text-violet-600" />
              Video Call
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Start a Google Meet session with a child. The call audio is recorded and automatically converted into a Day&apos;s Insights report.
            </p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* IDLE — pick child + start */}
          {callState === 'idle' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  Start a New Video Call
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Pick a child, open Google Meet, and share the link with the parent.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs sm:text-sm">Select Child</Label>
                  {loadingChildren ? (
                    <div className="h-11 flex items-center text-slate-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your assigned children…
                    </div>
                  ) : children.length === 0 ? (
                    <div className="h-11 flex items-center text-slate-500 text-sm">
                      No children assigned yet.
                    </div>
                  ) : (
                    <select
                      value={selectedChildId}
                      onChange={(e) => setSelectedChildId(e.target.value)}
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 mt-1"
                      data-testid="video-call-child-select"
                    >
                      <option value="">— choose a child —</option>
                      {children.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} {c.gender ? `(${c.gender})` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
                  <p className="font-medium mb-1">How it works:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-700/90">
                    <li>Pick a child and click <strong>Create Google Meet</strong>.</li>
                    <li>Google Meet opens in a new tab — copy the meeting link from there.</li>
                    <li>Paste the link back here, then share it with the parent.</li>
                    <li>Click <strong>Start Recording</strong>, conduct the session, then end to auto-generate the report.</li>
                  </ul>
                </div>

                <Button
                  onClick={generateLink}
                  disabled={!selectedChildId || children.length === 0}
                  className="bg-violet-600 hover:bg-violet-700 text-white h-11"
                  data-testid="video-call-generate-link-btn"
                >
                  <Link2 className="w-4 h-4 mr-2" /> Create Google Meet
                </Button>
              </CardContent>
            </Card>
          )}

          {/* AWAITING URL — observer pastes the Google Meet link */}
          {callState === 'awaiting_url' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-violet-600" />
                  Paste Your Google Meet Link
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Google Meet has opened in a new tab. Copy the meeting URL and paste it here.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
                  <p className="font-medium mb-1">Steps:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-amber-700/90">
                    <li>Switch to the Google Meet tab and start or join the meeting.</li>
                    <li>Copy the meeting link from the browser address bar.</li>
                    <li>Come back here, paste it below, and click <strong>Use This Link</strong>.</li>
                  </ol>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">Google Meet Link</Label>
                  <div className="flex items-stretch gap-2">
                    <input
                      type="url"
                      placeholder="https://meet.google.com/xxx-xxxx-xxx"
                      value={meetLinkInput}
                      onChange={(e) => { setMeetLinkInput(e.target.value); setError(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && confirmMeetUrl()}
                      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="video-call-meet-url-input"
                      autoFocus
                    />
                    <Button
                      onClick={confirmMeetUrl}
                      disabled={!meetLinkInput.trim()}
                      className="shrink-0 bg-violet-600 hover:bg-violet-700 text-white h-11"
                      data-testid="video-call-confirm-url-btn"
                    >
                      Use This Link
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('https://meet.google.com/new', '_blank', 'noopener,noreferrer')}
                    data-testid="video-call-reopen-meet-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open Google Meet Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setCallState('idle'); setMeetLinkInput(''); setError(''); }}
                    className="text-slate-500"
                    data-testid="video-call-awaiting-back-btn"
                  >
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* LINK READY — share the confirmed Google Meet link */}
          {callState === 'link_ready' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-violet-600" />
                  Share the Meeting Link
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Send this Google Meet link to <strong>{selectedChild?.name || 'the child'}</strong> or their parent.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Link box */}
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={roomUrl}
                    onClick={(e) => e.target.select()}
                    className="flex h-11 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm font-mono ring-offset-background"
                    data-testid="video-call-link-input"
                  />
                  <Button
                    variant="outline"
                    onClick={copyInviteLink}
                    className="shrink-0 h-11"
                    data-testid="video-call-link-copy-btn"
                  >
                    {linkCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>

                {parentNotified && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1 w-fit">
                    <Check className="w-3 h-3" />
                    Parent notified via WhatsApp &amp; Email
                  </div>
                )}

                {/* Share buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const text = `Hi! Please join ${selectedChild?.name || 'your child'}'s Google Meet session: ${roomUrl}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
                    }}
                    className="text-slate-600"
                    data-testid="video-call-link-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-1.5" /> Share on WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const subject = `Google Meet session — ${selectedChild?.name || ''}`.trim();
                      const body = `Hi,\n\nPlease join the Google Meet session using the link below:\n\n${roomUrl}\n\nJust tap the link to join from any device.\n\nThank you,\nSanjaya Team`;
                      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                    }}
                    className="text-slate-600"
                    data-testid="video-call-link-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-1.5" /> Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(roomUrl, '_blank', 'noopener,noreferrer')}
                    className="text-slate-600"
                    data-testid="video-call-link-preview-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Open Meeting
                  </Button>
                </div>

                <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800">
                  Once the student has joined, click <strong>Start Recording</strong> below. The browser will record your microphone while you conduct the session in Google Meet.
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                  <Button
                    onClick={joinAndRecord}
                    className="bg-violet-600 hover:bg-violet-700 text-white h-11"
                    data-testid="video-call-join-record-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" /> Start Recording
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setCallState('awaiting_url'); setRoomUrl(''); setRoomName(''); setError(''); setParentNotified(false); }}
                    className="text-slate-500"
                    data-testid="video-call-link-back-btn"
                  >
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* CONNECTING */}
          {callState === 'connecting' && (
            <Card>
              <CardContent className="py-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-violet-600 mb-3" />
                <p className="font-medium text-slate-700">Requesting microphone access…</p>
              </CardContent>
            </Card>
          )}

          {/* LIVE — recording active, Google Meet in separate tab */}
          {callState === 'live' && (
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">Recording — {formatTime(elapsed)}</div>
                      <div className="text-xs text-slate-500">
                        with {selectedChild?.name || 'child'} · Meet code: <code className="bg-slate-100 px-1.5 py-0.5 rounded">{roomName}</code>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyInviteLink}
                      data-testid="video-call-copy-link"
                    >
                      {linkCopied ? <Check className="w-4 h-4 mr-1.5 text-green-600" /> : <Copy className="w-4 h-4 mr-1.5" />}
                      {linkCopied ? 'Copied!' : 'Copy link'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={endCall}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="video-call-end-btn"
                    >
                      <PhoneOff className="w-4 h-4 mr-1.5" /> End &amp; Generate Report
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelCall}
                      className="text-slate-500"
                      data-testid="video-call-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Google Meet open-in-tab card */}
              <Card>
                <CardContent className="py-10 text-center space-y-4">
                  <div className="w-14 h-14 mx-auto bg-violet-50 rounded-full flex items-center justify-center">
                    <Video className="w-7 h-7 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Google Meet session is in progress</p>
                    <p className="text-sm text-slate-500 mt-1">
                      The meeting is open in a separate tab. Switch back to that tab to see the video.
                    </p>
                  </div>
                  <Button
                    onClick={() => window.open(roomUrl, '_blank', 'noopener,noreferrer')}
                    variant="outline"
                    data-testid="video-call-open-newtab"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Return to Google Meet
                  </Button>
                </CardContent>
              </Card>

              <div className="text-xs text-slate-500 flex items-start gap-2 px-1">
                {recording ? <Mic className="w-3.5 h-3.5 text-red-500 mt-0.5" /> : <MicOff className="w-3.5 h-3.5 mt-0.5" />}
                <span>
                  Your microphone is being recorded in the background. When done, come back here and click <strong>End &amp; Generate Report</strong>.
                </span>
              </div>
            </div>
          )}

          {/* UPLOADING */}
          {callState === 'uploading' && (
            <Card>
              <CardContent className="py-12 text-center space-y-3">
                <Loader2 className="w-10 h-10 animate-spin mx-auto text-violet-600" />
                <p className="font-medium text-slate-700">Uploading recording and generating report…</p>
                <p className="text-sm text-slate-500">Whisper transcribes, then the AI builds the Day&apos;s Insights — this can take a few minutes for longer sessions.</p>
              </CardContent>
            </Card>
          )}

          {/* DONE */}
          {callState === 'done' && (
            <Card>
              <CardContent className="py-10 text-center space-y-4">
                <div className="w-14 h-14 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                  <Check className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Report Generated!</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                    The session has been transcribed and analysed. The report is now in the
                    <strong className="text-slate-700"> Principal's review queue</strong> — once
                    approved, it will appear on the parent's dashboard automatically.
                  </p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 max-w-sm mx-auto text-left">
                  <p className="font-medium mb-1">What happens next:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-amber-700/90">
                    <li>Principal reviews &amp; approves the report.</li>
                    <li>Report becomes visible in the Parent portal.</li>
                    <li>Parent receives a WhatsApp notification.</li>
                  </ol>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                  <Button
                    onClick={() => navigate('/observer/reports')}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="video-call-my-reports-btn"
                  >
                    <FileText className="w-4 h-4 mr-1.5" /> My Reports
                  </Button>
                  {reportId && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/observer/report-detail/${reportId}`)}
                      data-testid="video-call-view-report-btn"
                    >
                      View This Report
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setCallState('idle');
                      setReportId(null);
                      setRoomName('');
                      setRoomUrl('');
                      setMeetLinkInput('');
                      setElapsed(0);
                      setError('');
                      setParentNotified(false);
                    }}
                    className="text-slate-500"
                    data-testid="video-call-new-btn"
                  >
                    Start another call
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </main>
    </div>
  );
};

export default ObserverVideoCall;
